import { useTranslation } from "react-i18next";
import type { TimelineEvent } from "@/api/types";

/**
 * T2 5-step pipeline indicator (Ingest -> Route -> Transcribe -> LLM -> Consensus).
 *
 * Renders in the meeting-card header, NOT in a tab — operators must see
 * the current phase at a glance. State is derived from the
 * `GET /api/meetings/:id/timeline` event log; no in-phase progress (%) is
 * shown — only transitions, per task spec.
 *
 * State precedence per phase (highest wins):
 *   error > done > skipped > processing > pending
 *
 * The "partial" state is reserved for phase 5 (consensus): when the latest
 * analysis-version status is `partial` we paint phase 5 yellow regardless
 * of what the event log says, because the FSM (ADR-003) is authoritative
 * for the consensus-only retry case.
 */

export type PhaseState =
  | "pending"
  | "processing"
  | "done"
  | "skipped"
  | "error"
  | "partial";

export interface PipelineProgressBlockProps {
  events: TimelineEvent[];
  /** Optional FSM override for phase 5; see component docstring. */
  consensusOverride?: PhaseState;
  /**
   * Bonus med (review): operators kept reading "5x ожидает" as "the
   * pipeline is broken" while the timeline endpoint was still loading.
   * Surfacing the loading vs awaiting-start distinction explicitly
   * removes that ambiguity.
   *
   * Truthy: render a skeleton (5 pulsing placeholder cells).
   * Falsy + empty events: render an "awaiting start" hint.
   * Falsy + non-empty events: render the normal pill row.
   */
  isLoading?: boolean;
}

interface StepDef {
  phase: 1 | 2 | 3 | 4 | 5;
  i18nKey: string;
}

const STEPS: ReadonlyArray<StepDef> = [
  { phase: 1, i18nKey: "meetings.card.pipeline.ingest" },
  { phase: 2, i18nKey: "meetings.card.pipeline.route" },
  { phase: 3, i18nKey: "meetings.card.pipeline.transcribe" },
  { phase: 4, i18nKey: "meetings.card.pipeline.llm" },
  { phase: 5, i18nKey: "meetings.card.pipeline.consensus" },
];

/**
 * Reduce a flat event stream into one state per phase. Latest event for the
 * phase wins via the precedence above — duplicate `done` markers (the
 * legacy `pipeline_done` row and the orchestrator emit) collapse cleanly.
 */
export function deriveStates(events: TimelineEvent[]): Map<number, PhaseState> {
  const out = new Map<number, PhaseState>();
  for (const e of events) {
    const current = out.get(e.phase) ?? "pending";
    out.set(e.phase, mergeState(current, mapEventStatus(e.status)));
  }
  return out;
}

function mapEventStatus(status: TimelineEvent["status"]): PhaseState {
  switch (status) {
    case "start":
      return "processing";
    case "done":
      return "done";
    case "error":
      return "error";
    case "skipped":
      return "skipped";
  }
}

function mergeState(prev: PhaseState, next: PhaseState): PhaseState {
  // Error always sticks. Done overrides processing/pending. Skipped is only
  // assigned when no other terminal state is set. Processing overrides
  // pending. This prevents a late `start` event (network reorder) from
  // hiding a completed phase.
  const rank: Record<PhaseState, number> = {
    pending: 0,
    processing: 1,
    skipped: 2,
    done: 3,
    partial: 4,
    error: 5,
  };
  return rank[next] >= rank[prev] ? next : prev;
}

export function PipelineProgressBlock({
  events,
  consensusOverride,
  isLoading,
}: PipelineProgressBlockProps) {
  const { t } = useTranslation();
  // Loading skeleton: same shape (5 pills) so the layout doesn't jump when
  // the data lands. ``role=status`` + ``aria-busy`` lets screen readers
  // announce "loading" without us repeating it in the label.
  if (isLoading) {
    return (
      <ol
        className="flex flex-wrap items-center gap-2 text-sm"
        data-testid="pipeline-progress-skeleton"
        role="status"
        aria-busy="true"
        aria-label={t("meetings.card.pipeline.loadingLabel")}
      >
        {STEPS.map((step) => (
          <li
            key={step.phase}
            className="inline-flex h-7 w-32 animate-pulse items-center gap-1.5 rounded-md border border-muted bg-muted/40 px-2 py-1"
          >
            <span className="sr-only">{t(step.i18nKey)}</span>
          </li>
        ))}
      </ol>
    );
  }
  // Post-load, no events at all: the pipeline hasn't been kicked off (e.g.
  // an upload with status=pending that never reached the worker). Render a
  // single hint instead of five "ожидает" pills — operators were reading
  // that as "pipeline is stuck".
  if (events.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="pipeline-progress-empty"
      >
        {t("meetings.card.pipeline.awaitingStart")}
      </p>
    );
  }
  const states = deriveStates(events);
  return (
    <ol
      className="flex flex-wrap items-center gap-2 text-sm"
      data-testid="pipeline-progress"
      aria-label={t("meetings.card.pipeline.indicatorLabel")}
    >
      {STEPS.map((step, idx) => {
        const state =
          step.phase === 5 && consensusOverride !== undefined
            ? consensusOverride
            : (states.get(step.phase) ?? "pending");
        return (
          <li
            key={step.phase}
            data-phase={step.phase}
            data-state={state}
            className={cellClasses(state)}
            aria-current={state === "processing" ? "step" : undefined}
          >
            <span className="font-mono text-xs">{step.phase}</span>
            <span>{t(step.i18nKey)}</span>
            <span className="text-xs opacity-70">
              {t(`meetings.card.pipeline.state.${state}`)}
            </span>
            {idx < STEPS.length - 1 ? (
              <span aria-hidden="true" className="opacity-40">
                {"→"}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function cellClasses(state: PhaseState): string {
  const base =
    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors";
  // Static class strings only — Tailwind purges anything dynamic, so we
  // enumerate every state instead of building names with template literals.
  switch (state) {
    case "pending":
      return `${base} border-muted bg-muted/30 text-muted-foreground`;
    case "processing":
      return `${base} border-sky-300 bg-sky-50 text-sky-900 animate-pulse`;
    case "done":
      return `${base} border-emerald-300 bg-emerald-50 text-emerald-900`;
    case "skipped":
      return `${base} border-muted bg-muted/20 text-muted-foreground italic`;
    case "partial":
      return `${base} border-amber-300 bg-amber-50 text-amber-900`;
    case "error":
      return `${base} border-rose-300 bg-rose-50 text-rose-900`;
  }
}
